import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui-kit/dialog';
import { StatusBadge } from '@/modules/service-requests/shared/components/status-badge';
import { formatOrgDateTime } from '@/lib/utils/custom-date/custom-date';
import { formatLetterDateOnly, NOC_COPY_TYPES } from '../../utils/noc-rules';
import { NocApplication, NocType } from '../../types/noc.types';

interface NocDetailsDialogProps {
  application: NocApplication | null;
  nocTypes: NocType[];
  onOpenChange: (open: boolean) => void;
}

const dash = (value: string) => (value === 'N/A' ? '' : value);

export const NocDetailsDialog = ({
  application,
  nocTypes,
  onOpenChange,
}: NocDetailsDialogProps) => {
  const { t } = useTranslation();

  const copyType = NOC_COPY_TYPES.find((option) => option.value === application?.NocCopyType);
  const rows: [string, string | undefined][] = application
    ? [
        ['NAME', application.Name],
        ['DESIGNATION', application.Designation],
        ['NOC_TYPE', nocTypes.find((type) => type.ItemId === application.NocTypeId)?.Name],
        ['NOC_COPY_TYPE', copyType ? t(copyType.labelKey) : undefined],
        ['PASSPORT_NO', application.PassportNumber],
        ['TRAVEL_START_DATE', dash(formatLetterDateOnly(application.TravelStartDate))],
        ['TRAVEL_END_DATE', dash(formatLetterDateOnly(application.TravelEndDate))],
        ['TRAVEL_DESTINATION', application.TravelDestination],
        ['REASON', application.Reason],
        ['NOC_NUMBER', application.DocumentNumber],
        ['APPROVED_BY', application.ApprovedByName],
        [
          'APPROVAL_DATE_AND_TIME',
          formatOrgDateTime(application.ApprovedDateUtc, application.Timezone || undefined),
        ],
        ['REJECTION_NOTE', application.ApprovalStatusReason],
        [
          'LAST_MODIFIED',
          formatOrgDateTime(application.LastUpdatedDate, application.Timezone || undefined),
        ],
      ]
    : [];

  return (
    <Dialog open={Boolean(application)} onOpenChange={onOpenChange}>
      <DialogContent
        alignCloseWithTitle
        className="grid-cols-[minmax(0,1fr)] max-h-[90dvh] overflow-y-auto sm:max-w-lg"
      >
        <DialogHeader className="pr-6">
          <DialogTitle className="break-words">{t('NOC_REVIEW_DETAILS')}</DialogTitle>
        </DialogHeader>
        <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-[auto_minmax(0,1fr)] sm:gap-y-2">
          {rows.map(([labelKey, value]) => (
            <div key={labelKey} className="flex flex-col gap-0.5 sm:contents">
              <dt className="text-muted-foreground">{t(labelKey)}</dt>
              <dd className="min-w-0 whitespace-pre-line break-words font-medium">
                {value || '-'}
              </dd>
            </div>
          ))}
          {application && (
            <div className="flex flex-col gap-0.5 sm:contents">
              <dt className="text-muted-foreground">{t('STATUS')}</dt>
              <dd>
                <StatusBadge status={application.Status} />
              </dd>
            </div>
          )}
        </dl>
      </DialogContent>
    </Dialog>
  );
};
